import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta


// Función para crear un feedback
const createFeedback = async (feedbackData: {
  name: string;
  email: string;
  telefone?: string;
  rating: number;
  comments?: string;
  termsAccepted: boolean;
}) => {
  const response = await axiosInstance.post('/feedbacks/create', feedbackData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear un feedback
export const useCreateFeedback = () => {
  const mutation = useMutation({
    mutationFn: createFeedback, // Función para crear el feedback
    onSuccess: () => {
      toast.success('Feedback enviado con éxito.');
    },
    onError: (error: any) => {
      // Verifica la existencia de un mensaje de error en la respuesta
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al enviar el feedback: ${errorMessage}`);
      console.error('Error al enviar el feedback:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // isPending es la propiedad correcta para mutaciones
    isSuccess: mutation.isSuccess,
    error: mutation.error?.response?.data?.message || mutation.error?.message, // Asegúrate de capturar el error correctamente
  };
};


// Función para obtener los feedbacks
const fetchFeedbacks = async () => {
  const response = await axiosInstance.get('/feedbacks/user');
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar los feedbacks
export const useGetFeedbacks = () => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['feedbacks'], // Esto es lo que identifica la consulta en React Query
    queryFn: async () => {
      try {
        return await fetchFeedbacks();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
        toast.error(`Error al obtener los feedbacks: ${errorMessage}`);
        console.error('Error al obtener los feedbacks:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Opcional: Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
  });

  return {
    feedbacks: data,   // Los datos obtenidos
    isLoading,         // Si está cargando
    isError,           // Si hubo un error
    error,             // El error completo (si existe)
  };
};



// Función para obtener los feedbacks
const fetchAllFeedbacks = async () => {
  const response = await axiosInstance.get('/feedbacks');
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar todos los feedbacks
export const useGetAllFeedbacks = () => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['feedbacks'], // Esto es lo que identifica la consulta en React Query
    queryFn: async () => {
      try {
        return await fetchAllFeedbacks();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
        toast.error(`Error al obtener los feedbacks: ${errorMessage}`);
        console.error('Error al obtener los feedbacks:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Opcional: Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
  });

  return {
    feedbacks: data,   // Los datos obtenidos
    isLoading,         // Si está cargando
    isError,           // Si hubo un error
    error,             // El error completo (si existe)
  };
};


// Función para eliminar un feedback
const deleteFeedback = async (feedbackId: string) => {
  const response = await axiosInstance.delete(`/feedbacks/${feedbackId}`);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para eliminar un feedback
export const useDeleteFeedback = () => {
  const mutation = useMutation({
    mutationFn: deleteFeedback, // Función para eliminar el feedback
    onSuccess: () => {
      toast.success('Feedback eliminado con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error desconocido';
      toast.error(`Error al eliminar el feedback: ${errorMessage}`);
      console.error('Error al eliminar el feedback:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // isPending es la propiedad correcta para mutaciones
    isSuccess: mutation.isSuccess,
  };
};
